import { Injectable } from '@angular/core';
import {
  APIService,
  CommercialAgreement,
  CreateCommercialAgreementInput,
  ListCommercialAgreementsQuery,
  UpdateCommercialAgreementInput,
} from 'src/app/app-sync.service';
import { UsersService } from '../users/users.service';

@Injectable({
  providedIn: 'root',
})
export class CommercialAgreementsService {
  constructor(private api: APIService) {}

  /**
   * Obtiene la lista de especificaciones de Acuerdos Comerciales desde el API sin modificar los objetos.
   * @param {string} business Negocio
   * @param {ModelIDKeyConditionInput} modelIDKeyConditionInput Filtro
   * @return {Promise<CommercialAgreement[]>} Lista de objetos CommercialAgreements
   */
  private async getCommercialAgreementsListRaw(
    business: string
  ): Promise<CommercialAgreement[]> {
    let tempListDocumentSpecs: CommercialAgreement[] = [];
    let listDocumentsSpecsResult: ListCommercialAgreementsQuery =
      await this.api.CommercialAgreementsByBusinessAndAgreementId(business);
    tempListDocumentSpecs = <CommercialAgreement[]>(
      listDocumentsSpecsResult.items
    );

    let nextToken = listDocumentsSpecsResult.nextToken;
    // Sigue obteniendo resultados si hay un nextToken
    while (nextToken) {
      let loopListDocumentsSpecsResult: ListCommercialAgreementsQuery =
        await this.api.CommercialAgreementsByBusinessAndAgreementId(
          business,
          {},
          undefined,
          {},
          100,
          nextToken
        );
      tempListDocumentSpecs.push(
        ...(<CommercialAgreement[]>loopListDocumentsSpecsResult.items)
      );
      nextToken = loopListDocumentsSpecsResult.nextToken;
    }
    tempListDocumentSpecs.forEach((x) => {
      if (x.comment) {
        x.comment =
          x.comment && x.comment !== '{}' ? JSON.parse(x.comment) : null;
      }
    });
    return tempListDocumentSpecs; // Retorna la lista sin modificar
  }

  /**
   * Obtiene la lista de especificaciones de documentos con un filtro que incluye selectedCenter.
   * @param {string} business Negocio
   * @return {Promise<CommercialAgreement[]>} Lista de objetos CommercialAgreement
   */
  async getCommercialAgreementList(
    business: string
  ): Promise<CommercialAgreement[]> {
    return this.getCommercialAgreementsListRaw(business.toUpperCase());
  }

  async updateCommercialAgreement(
    commercialAgreement: UpdateCommercialAgreementInput
  ) {
    await this.api.UpdateCommercialAgreement(commercialAgreement);
  }

  async createCommercialAgreement(
    commercialAgreement: CreateCommercialAgreementInput
  ) {
    await this.api.CreateCommercialAgreement(commercialAgreement);
  }

  async getEntitybyPlate(plate: string) {
    if (!plate || plate.trim() === '') {
      console.warn('Placa inválida');
      return null;
    }
    try {
      const [cisterna, vehicle, tracto, envasado, semirremolque, tanque] =
        await Promise.all([
          this.api.GetCisterna(plate),
          this.api.GetVehicle(plate),
          this.api.GetTracto(plate),
          this.api.GetEnvasado(plate),
          this.api.GetSemirremolque(plate),
          this.api.GetTanque(plate),
        ]);

      const responses = [
        cisterna,
        vehicle,
        tracto,
        envasado,
        semirremolque,
        tanque,
      ];
      const validResponse = responses.find((res) => res != null);

      if (validResponse) {
        return validResponse;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error al obtener entidad por placa:', error);
      throw error;
    }
  }

  async getBySapId(sapId: string) {
    const response = await this.api.GetExternalIdMapping(sapId);
    if (!response || !response.modelTypeName) {
      return null;
    }
    type ModelKeys =
      | 'envasado'
      | 'semirremolque'
      | 'cisterna'
      | 'vehicle'
      | 'tanque';
    const validKeys: ModelKeys[] = [
      'envasado',
      'semirremolque',
      'cisterna',
      'vehicle',
      'tanque',
    ];
    const key = response.modelTypeName.toLowerCase() as ModelKeys;
    if (!validKeys.includes(key as ModelKeys)) {
      throw new Error('Acuerdo comercial no está permitido para esta entidad.');
    }
    const modelObject = response[key];
    return modelObject || null;
  }
}
